import { useState, useEffect } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Calendar, Facebook, ArrowRight, Phone, MessageCircle, Link2, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import KennelSEO, { animalBreederSchema, breadcrumbSchema } from "./KennelSEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api/kennel";
const BASE = "https://wolfevomkatzenberg.pl";
const WA = "https://wa.me/48663746095?text=" + encodeURIComponent("Dzień dobry, jestem zainteresowany/a szczeniakami z hodowli Wölfe vom Katzenberg.");

const formatDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return d; }
};

// Inline markdown: **bold** and [label](href) internal (React Router) / external links
const renderInline = (text) => {
  const out = [];
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0, m, k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(<strong key={`b${k++}`} className="text-[#F5F5F5] font-medium">{m[1]}</strong>);
    } else if (m[3].startsWith("/")) {
      out.push(<Link key={`l${k++}`} to={m[3]} className="text-[#D4AF37] underline underline-offset-4 decoration-[#D4AF37]/40 hover:decoration-[#D4AF37] transition-colors">{m[2]}</Link>);
    } else {
      out.push(<a key={`a${k++}`} href={m[3]} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline underline-offset-4 decoration-[#D4AF37]/40 hover:decoration-[#D4AF37] transition-colors">{m[2]}</a>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

// Render content supporting ## H2, ### H3, **bold**, links, and single-line breaks
const renderContent = (content) => {
  const blocks = (content || "").split(/\n{2,}/).filter(Boolean);
  return blocks.map((block, i) => {
    const t = block.trim();
    if (t.startsWith("### ")) return (
      <h3 key={i} className="font-serif text-xl md:text-2xl text-[#F5F5F5] mt-12 mb-4 leading-snug tracking-tight">{renderInline(t.replace(/^###\s+/, ""))}</h3>
    );
    if (t.startsWith("## ")) return (
      <h2 key={i} className="font-serif text-[1.6rem] md:text-[2rem] text-[#F5F5F5] mt-16 mb-6 leading-[1.2] tracking-tight">
        <span className="block w-10 h-[2px] bg-[#D4AF37] mb-5" />
        {renderInline(t.replace(/^##\s+/, ""))}
      </h2>
    );
    if (/^- /m.test(t) && t.split("\n").every(ln => ln.trim().startsWith("- "))) return (
      <ul key={i} className="mb-7 space-y-3">
        {t.split("\n").map((ln, j) => (
          <li key={j} className="flex items-start gap-3 text-[#CFCFCF] text-[1.075rem] md:text-[1.18rem] leading-[1.9] font-light">
            <span className="mt-[0.85em] w-1.5 h-1.5 shrink-0 rounded-full bg-[#D4AF37]" />
            <span>{renderInline(ln.trim().replace(/^-\s+/, ""))}</span>
          </li>
        ))}
      </ul>
    );
    const lines = block.split("\n");
    return (
      <p key={i} className="text-[#CFCFCF] text-[1.075rem] md:text-[1.18rem] leading-[2.05] mb-7 font-light">
        {lines.map((ln, j) => (
          <span key={j}>{renderInline(ln)}{j < lines.length - 1 ? <br /> : null}</span>
        ))}
      </p>
    );
  });
};

const KennelBlogPost = () => {
  useOutletContext();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");
  const [lightbox, setLightbox] = useState(null);
  const [coverDim, setCoverDim] = useState(null);
  const [copied, setCopied] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setStatus("loading");
    fetch(`${API}/blog/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setPost(d); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  useEffect(() => {
    fetch(`${API}/blog`)
      .then(r => r.ok ? r.json() : [])
      .then(list => setRelated((list || []).filter(p => p.slug !== slug).slice(0, 3)))
      .catch(() => setRelated([]));
  }, [slug]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      const len = post?.gallery?.length || 0;
      if (!len) return;
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowLeft") setLightbox(i => (i - 1 + len) % len);
      else if (e.key === "ArrowRight") setLightbox(i => (i + 1) % len);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, post]);

  if (status === "loading") {
    return <div className="min-h-[60vh] flex items-center justify-center"><p className="text-[#B8B8B8]/40 text-sm">Wczytywanie…</p></div>;
  }

  if (status === "notfound") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#F5F5F5] text-lg font-serif mb-4">Wpis nie znaleziony</p>
          <Link to="/z-zycia-hodowli" className="text-[#D4AF37] text-sm hover:underline">Wróć do „Z życia hodowli"</Link>
        </div>
      </div>
    );
  }

  const url = `${BASE}/z-zycia-hodowli/${post.slug}`;
  const ogImg = post.og_image || post.cover_image || `${BASE}/logo_katzenberg_new.png`;
  // Share the server-rendered OG endpoint so Facebook reliably scrapes title/description/image.
  // A version param (from updated_at/date) busts Facebook's aggressive URL cache when content changes.
  const ogVersion = (post.updated_at || post.date || "").replace(/[^0-9]/g, "").slice(0, 14);
  const ogShareUrl = `${BASE}/api/kennel/og/${post.slug}${ogVersion ? `?v=${ogVersion}` : ""}`;
  const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogShareUrl)}`;
  const handleCopyShare = async () => {
    try { await navigator.clipboard.writeText(ogShareUrl); } catch (e) {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.seo_description || post.excerpt || "",
    "image": ogImg.startsWith("http") ? ogImg : `${BASE}${ogImg}`,
    "datePublished": post.date,
    "dateModified": post.updated_at || post.date,
    "articleSection": post.category,
    "author": { "@type": "Organization", "name": "Wölfe vom Katzenberg" },
    "publisher": { "@type": "Organization", "name": "Wölfe vom Katzenberg", "logo": { "@type": "ImageObject", "url": `${BASE}/logo_katzenberg_new.png` } },
    "mainEntityOfPage": { "@type": "WebPage", "@id": url },
  };

  return (
    <div data-testid="blog-post-page">
      <KennelSEO
        title={post.seo_title || `${post.title} | Wölfe vom Katzenberg`}
        description={post.seo_description || post.excerpt}
        canonical={url}
        ogImage={ogImg.startsWith("http") ? ogImg : `${BASE}${ogImg}`}
        ogType="article"
        schema={[
          articleSchema,
          animalBreederSchema,
          breadcrumbSchema([
            { name: "Start", href: "/" },
            { name: "Z życia hodowli", href: "/z-zycia-hodowli" },
            { name: post.title, href: `/z-zycia-hodowli/${post.slug}` },
          ]),
        ]}
      />

      {/* Breadcrumb */}
      <div className="bg-[#141416] border-b border-[#2A2A2D] px-6 md:px-12 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-2 text-[10px] tracking-wider text-[#B8B8B8]/40 flex-wrap">
          <Link to="/" className="hover:text-[#D4AF37] transition-colors">START</Link>
          <span>/</span>
          <Link to="/z-zycia-hodowli" className="hover:text-[#D4AF37] transition-colors">Z ŻYCIA HODOWLI</Link>
          <span>/</span>
          <span className="text-[#D4AF37]/60 uppercase">{post.category}</span>
        </div>
      </div>

      {/* Header — magazine style */}
      <section className="bg-[#141416] px-6 md:px-12 pt-14 pb-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Link to="/z-zycia-hodowli" className="text-[11px] tracking-[0.15em] text-[#B8B8B8]/40 hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2 mb-9" data-testid="back-to-blog">
              <ArrowLeft className="w-3.5 h-3.5" /> Z ŻYCIA HODOWLI
            </Link>
            {post.category && (
              <div className="mb-6">
                <span className="inline-block text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-1.5" data-testid="blog-post-category">{post.category}</span>
              </div>
            )}
            <h1 className="font-serif text-[clamp(1.9rem,4vw,3.25rem)] text-[#F5F5F5] leading-[1.12] mb-6" data-testid="blog-post-title">{post.title}</h1>
            {post.excerpt && (
              <p className="text-[#B8B8B8] text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-7" data-testid="blog-post-lead">{post.excerpt}</p>
            )}
            <div className="flex items-center justify-center gap-3 text-[11px] tracking-[0.15em] uppercase text-[#B8B8B8]/45">
              <span className="w-8 h-px bg-[#D4AF37]/30" />
              {post.date && <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#D4AF37]/60" /> {formatDate(post.date)}</span>}
              <span className="w-8 h-px bg-[#D4AF37]/30" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cover — adapts to image ratio: wide photos fill 16:9, square/portrait graphics show fully (never cropped) */}
      {post.cover_image && (() => {
        const wide = coverDim ? coverDim.w / coverDim.h >= 1.4 : true;
        return (
        <section className="px-6 md:px-12 bg-gradient-to-b from-[#141416] to-[#0B0B0D]">
          <div className={`${wide ? "max-w-5xl" : "max-w-xl"} mx-auto`}>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
              className="p-2 md:p-2.5 bg-[#141416] border border-[#2A2A2D] shadow-2xl shadow-black/50 -translate-y-0 md:-translate-y-3">
              <div className="overflow-hidden" style={{ aspectRatio: coverDim ? `${coverDim.w} / ${coverDim.h}` : "16 / 9" }}>
                <img src={post.cover_image} alt={post.image_alt_text || post.title}
                  onLoad={(e) => setCoverDim({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                  className="w-full h-full object-cover object-center" data-testid="blog-post-cover" />
              </div>
            </motion.div>
          </div>
        </section>
        );
      })()}

      {/* Content */}
      <section className="px-6 md:px-12 py-14 md:py-20 bg-[#0B0B0D]">
        <div className="max-w-[760px] mx-auto">
          <motion.article initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            data-testid="blog-post-content">
            {renderContent(post.content)}
          </motion.article>

          {/* Gallery — premium even grid */}
          {Array.isArray(post.gallery) && post.gallery.length > 0 && (
            <div className="mt-20" data-testid="blog-post-gallery">
              <div className="flex items-center gap-4 mb-8">
                <span className="text-[11px] tracking-[0.3em] text-[#D4AF37] uppercase">Galeria</span>
                <span className="flex-1 h-px bg-[#2A2A2D]" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
                {post.gallery.map((g, i) => (
                  <figure key={i} data-testid={`blog-gallery-${i}`} className="group">
                    <button onClick={() => setLightbox(i)}
                      className="block w-full aspect-[4/3] overflow-hidden bg-[#0B0B0D] border border-[#2A2A2D] group-hover:border-[#D4AF37]/50 transition-all duration-300 shadow-lg shadow-black/30">
                      <img src={g.url} alt={g.alt || `${post.title} — zdjęcie ${i + 1}`} className="w-full h-full object-contain group-hover:scale-[1.05] transition-transform duration-700" />
                    </button>
                    {g.caption && (
                      <figcaption className="mt-2.5 text-center text-[#B8B8B8]/70 text-xs md:text-sm tracking-wide" data-testid={`blog-gallery-caption-${i}`}>
                        {g.caption}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </div>
          )}

          {/* Videos */}
          {Array.isArray(post.videos) && post.videos.length > 0 && (
            <div className="mt-20" data-testid="blog-post-videos">
              <div className="flex items-center gap-4 mb-8">
                <span className="text-[11px] tracking-[0.3em] text-[#D4AF37] uppercase">Filmy</span>
                <span className="flex-1 h-px bg-[#2A2A2D]" />
              </div>
              <div className={`grid gap-5 ${post.videos.length > 1 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
                {post.videos.map((v, i) => (
                  <figure key={i} data-testid={`blog-video-${i}`}>
                    <div className="w-full aspect-video overflow-hidden bg-[#0B0B0D] border border-[#2A2A2D] shadow-lg shadow-black/30">
                      <video src={v.url} controls preload="metadata" playsInline className="w-full h-full object-contain" />
                    </div>
                    {v.caption && (
                      <figcaption className="mt-2.5 text-center text-[#B8B8B8]/70 text-xs md:text-sm tracking-wide" data-testid={`blog-video-caption-${i}`}>
                        {v.caption}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </div>
          )}

          {/* Facebook share */}
          <div className="mt-16 pt-8 border-t border-[#2A2A2D]">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-[#B8B8B8]/50 text-xs tracking-wider uppercase">Spodobał Ci się wpis?</span>
              <button type="button" onClick={() => window.open(fbShare, "_blank", "noopener,noreferrer")}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1877F2] text-white text-xs tracking-[0.1em] uppercase font-medium hover:bg-[#1465d1] transition-colors"
                data-testid="blog-fb-share">
                <Facebook className="w-4 h-4" /> Udostępnij na Facebooku
              </button>
              <button type="button" onClick={handleCopyShare}
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#2A2A2D] text-[#B8B8B8] text-xs tracking-[0.1em] uppercase font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                data-testid="blog-copy-share">
                {copied ? <><Check className="w-4 h-4" /> Skopiowano</> : <><Link2 className="w-4 h-4" /> Kopiuj link</>}
              </button>
            </div>
            <p className="mt-3 text-[#B8B8B8]/40 text-xs leading-relaxed max-w-xl" data-testid="blog-share-hint">
              Wskazówka: aby na Facebooku pojawiło się zdjęcie z wpisu, udostępniaj przyciskiem powyżej lub skopiuj i wklej ten link — nie kopiuj adresu z paska przeglądarki.
            </p>
          </div>

          {/* Internal links */}
          <div className="mt-12">
            <h2 className="text-[11px] tracking-[0.3em] text-[#D4AF37] mb-5 uppercase">Zobacz też</h2>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
              {[
                { to: "/szczenieta", label: "Szczenięta" },
                { to: "/mioty", label: "Mioty" },
                { to: "/psy", label: "Nasze psy" },
                { to: "/o-hodowli", label: "O hodowli" },
              ].map(l => (
                <Link key={l.to} to={l.to}
                  className="text-center px-5 py-3 sm:py-2.5 border border-[#D4AF37]/20 text-[#D4AF37] text-xs tracking-[0.15em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* More articles — related posts */}
      {related.length > 0 && (
        <section className="bg-[#0B0B0D] border-t border-[#2A2A2D] px-6 md:px-12 py-16 md:py-20" data-testid="blog-related">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-4 mb-10">
              <span className="text-[11px] tracking-[0.3em] text-[#D4AF37] uppercase">Więcej z życia hodowli</span>
              <span className="flex-1 h-px bg-[#2A2A2D]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {related.map((r, i) => (
                <motion.div key={r.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                  <Link to={`/z-zycia-hodowli/${r.slug}`} className="group block" data-testid={`blog-related-${i}`}>
                    <div className="aspect-square overflow-hidden bg-[#141416] border border-[#2A2A2D] group-hover:border-[#D4AF37]/50 transition-all duration-300 shadow-lg shadow-black/30">
                      {r.cover_image && (
                        <img src={r.cover_image} alt={r.image_alt_text || r.title} loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                      )}
                    </div>
                    {r.category && (
                      <span className="inline-block mt-4 text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]/80">{r.category}</span>
                    )}
                    <h3 className="font-serif text-lg md:text-xl text-[#F5F5F5] leading-snug mt-2 group-hover:text-[#D4AF37] transition-colors">{r.title}</h3>
                    {r.date && <p className="mt-2 text-[11px] tracking-[0.12em] uppercase text-[#B8B8B8]/40">{formatDate(r.date)}</p>}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#141416] border-t border-[#2A2A2D] px-6 md:px-12 py-16">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F5] mb-4">Zainteresowany szczeniakami z naszej hodowli?</h2>
          <p className="text-[#B8B8B8]/70 text-sm md:text-base mb-8 leading-relaxed">Napisz do nas — chętnie opowiemy o planowanych miotach i dostępnych szczeniętach owczarka niemieckiego.</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href={WA} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3 bg-[#25D366] text-white text-xs tracking-[0.15em] uppercase font-semibold hover:bg-[#1eb455] transition-colors" data-testid="blog-cta-whatsapp">
              <MessageCircle className="w-4 h-4" /> Napisz na WhatsApp
            </a>
            <Link to="/kontakt" className="inline-flex items-center gap-2 px-8 py-3 bg-[#D4AF37] text-[#0B0B0D] text-xs tracking-[0.15em] uppercase font-semibold hover:bg-[#C8A96A] transition-colors" data-testid="blog-cta-contact">
              Kontakt z hodowlą <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:+48663746095" className="inline-flex items-center gap-2 px-8 py-3 border border-[#2A2A2D] text-[#B8B8B8] text-xs tracking-[0.15em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
              <Phone className="w-4 h-4" /> 663 746 095
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox with prev/next arrows */}
      <AnimatePresence>
        {lightbox !== null && post.gallery && post.gallery[lightbox] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4 md:p-10" onClick={() => setLightbox(null)} data-testid="blog-lightbox">
            <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 z-50 w-12 h-12 bg-[#1A1A1D] border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors" data-testid="blog-lightbox-close" aria-label="Zamknij"><X className="w-5 h-5" /></button>
            {post.gallery.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); setLightbox(i => (i - 1 + post.gallery.length) % post.gallery.length); }} className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-50 w-12 h-12 md:w-14 md:h-14 bg-[#1A1A1D] border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#D4AF37] transition-colors" data-testid="blog-lightbox-prev" aria-label="Poprzednie"><ChevronLeft className="w-6 h-6" /></button>
                <button onClick={(e) => { e.stopPropagation(); setLightbox(i => (i + 1) % post.gallery.length); }} className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-50 w-12 h-12 md:w-14 md:h-14 bg-[#1A1A1D] border border-[#2A2A2D] flex items-center justify-center text-[#B8B8B8] hover:text-[#D4AF37] transition-colors" data-testid="blog-lightbox-next" aria-label="Następne"><ChevronRight className="w-6 h-6" /></button>
              </>
            )}
            <div className="flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
              <motion.img key={post.gallery[lightbox].url} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}
                src={post.gallery[lightbox].url} alt={post.gallery[lightbox].alt || post.title} className="max-w-[92vw] max-h-[78vh] object-contain" data-testid="blog-lightbox-img" />
              {post.gallery[lightbox].caption && (
                <p className="text-[#B8B8B8]/80 text-sm text-center" data-testid="blog-lightbox-caption">{post.gallery[lightbox].caption}</p>
              )}
            </div>
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#B8B8B8]/40 text-[11px] tracking-[0.2em]" data-testid="blog-lightbox-counter">{lightbox + 1} / {post.gallery.length}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KennelBlogPost;
